import { useEffect, useState } from 'react';
import { getListingTimezone } from '../utils/listingTimezone';

type ListingLike = {
  lat?: number | null;
  lng?: number | null;
  state?: string | null;
};

/**
 * Resolves a property's IANA timezone from its coordinates.
 *
 * A state-code map gets the wrong answer for a straddle state — Tennessee
 * spans Eastern and Central, so an Eastern-TN listing mapped by state alone
 * reads as Central, and a closing time picked against that zone is stored an
 * hour off. tz-lookup carries a ~1MB coordinate dataset that is dynamically
 * imported (see listingTimezone.ts), so resolution is async; this returns
 * `undefined` until it resolves, and forever for a listing with no lat/lng
 * or no listing at all — callers should fall back to the coarser
 * `getListingTimezoneSync` state map for that gap, never to the viewer's own
 * timezone, since contract dates belong to the property.
 */
export const usePropertyTimezone = (
  listing: ListingLike | null | undefined
): string | undefined => {
  const [propertyTimezone, setPropertyTimezone] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (!listing) {
      setPropertyTimezone(undefined);
      return;
    }
    let cancelled = false;
    getListingTimezone(listing).then((tz) => {
      if (!cancelled) setPropertyTimezone(tz);
    });
    return () => {
      cancelled = true;
    };
  }, [listing]);

  return propertyTimezone;
};
